/**
 * Adapter: definicja komponentu dynamicznego (tablePanel/grid) → props WiseTable
 * Używany w DynamicPage do renderowania tabel przez WiseTable.
 */

import type { IComponentParams } from 'plugins/api/types';
import type { IDynamicComponent } from 'plugins/api/types';
import type { IWiseTableProps, IWiseTableParameter, IWiseTableFilter, IWiseTableColumn, IWiseTableAction } from 'components/wise/types';

const DEFAULT_ROW_KEY = 'id';
const DEFAULT_LIMIT = 20;
const DEFAULT_NAME_COLUMN_WIDTH = 380;
const DEFAULT_ID_COLUMN_WIDTH = 150;

export interface IDynamicComponentToWiseTableParams {
  component: IDynamicComponent;
  pageSymbol: string;
  requestParams?: object;
  componentParams?: IComponentParams;
  columnWidths?: Record<string, number>;
}

/**
 * Mapuje definicję komponentu tabeli z API (tablePanel/grid) na props dla WiseTable.
 * Typy z API (IDynamicFilter, IDynamicColumn, IDynamicAction) są kompatybilne
 * z IWiseTable* (rozszerzenia), więc używamy rzutowania.
 */
export function dynamicComponentToWiseTableProps({
  component,
  pageSymbol,
  requestParams,
  componentParams,
  columnWidths
}: IDynamicComponentToWiseTableParams): IWiseTableProps {
  const parameters = (component.filters ?? []) as IWiseTableParameter[];
  const filters = (component.filters ?? []) as IWiseTableFilter[];
  const columns: IWiseTableColumn[] = (component.columns ?? []).map((col) => {
    const base = col as IWiseTableColumn;
    const size =
      columnWidths?.[col.field_symbol] ??
      (col.field_symbol === 'name'
        ? DEFAULT_NAME_COLUMN_WIDTH
        : col.field_symbol === 'id'
          ? DEFAULT_ID_COLUMN_WIDTH
          : undefined);
    if (size === undefined) return base;
    return { ...base, size };
  });
  const actions = (component.actions ?? []) as IWiseTableAction[];

  return {
    label: component.label ?? '',
    pageSymbol,
    componentSymbol: component.symbol,
    parameters,
    filters,
    columns,
    actions,
    loadDataParams: component.load_data_params,
    requestParams,
    componentParams,
    defaultLimit: DEFAULT_LIMIT,
    rowKey: DEFAULT_ROW_KEY,
    showRecordCountInTitle: true,
    withAdvancedFilters: false
  };
}
